// src/pages/ProductPage.tsx
import React, {
    useMemo,
    useRef,
    useCallback,
    useEffect,
    useState,
    Fragment,
} from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { IconContext } from "react-icons";
import {
    FaFacebook,
    FaTelegram,
    FaLine,
    FaTwitter,
    FaWhatsapp,
    FaShieldHeart,
    FaLink,
} from "react-icons/fa6";
import { IoLogoWechat } from "react-icons/io5";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { useAuth } from "../context/AuthContext";
import { useCustomerContext } from "../context/customerContext";
import { hasRole } from "../utils/auth";
import {
    fetchProductById,
    fetchCustomer,
    fetchMtPrice,
} from "../utils/api";
import {
    Product,
    Customer,
    displayPrice,
    useHandleAddToCart,
    formatProductNameForUrl,
} from "../utils/types";

import {
    useQuery,
    useQueryClient,
    QueryKey,
} from "@tanstack/react-query";
import ModifierModal from '../components/ModifierModal'; // Import ModifierModal
import ProductDetails from '../components/ProductDetails'; // Import the new component
import CustomerProfileModal from '../components/CustomerProfileModal';
import { useTranslation } from 'react-i18next';


/* ------------------------------------------
   Local helpers & small in-file components
-------------------------------------------*/

// Fade-in container: adds opacity transition when "show" flips true
const FadeIn: React.FC<{ show: boolean; className?: string; children: React.ReactNode }> = ({
    show,
    className,
    children,
}) => {
    return (
        <div
            className={`transition-opacity duration-300 ease-out ${show ? "opacity-100" : "opacity-0"} ${className || ""}`}
        >
            {children}
        </div>
    );
};

// Loading Skeleton (kept close to your original)
const ProductSkeleton: React.FC = () => (
    <>
        <Helmet>
            <title>Loading Product...</title>
        </Helmet>
        <div className="container mx-auto p-4 animate-pulse">
            <div className="mb-4 h-6 bg-gray-200 rounded w-1/4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Image Skeleton */}
                <div>
                    <div className="w-full h-96 bg-gray-200 rounded shadow-md"></div>
                </div>

                {/* Details Skeleton */}
                <div className="space-y-4">
                    <div className="h-8 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>

                    {/* Price Skeleton */}
                    <div className="my-5 p-3 bg-gray-100 rounded space-y-2">
                        <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                        <div className="h-5 bg-gray-200 rounded w-1/3"></div>
                    </div>

                    {/* Description Skeleton */}
                    <div className="space-y-2">
                        <div className="h-5 bg-gray-200 rounded w-1/4"></div>
                        <div className="h-4 bg-gray-200 rounded w-full"></div>
                        <div className="h-4 bg-gray-200 rounded w-full"></div>
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    </div>

                    {/* Button Skeleton */}
                    <div className="h-12 bg-gray-200 rounded w-full"></div>
                </div>
            </div>
        </div>
    </>
);

const SocialShareButtons: React.FC<{
    url: string;
    text: string;
}> = ({ url, text }) => {
    const encodedUrl = encodeURIComponent(url);
    const shareText = encodeURIComponent(text);

    return (
        <div className="social-share flex flex-row md:flex-col justify-center mt-0  gap-4 ">
            <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mr-2"
                aria-label="Share on Facebook"
            >
                <IconContext.Provider value={{ size: "1.6em", color: "#1877F2" }}>
                    <FaFacebook />
                </IconContext.Provider>
            </a>

            <a
                href={`https://social-plugins.line.me/lineit/share?url=${encodedUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mr-2"
                aria-label="Share on LINE"
            >
                <IconContext.Provider value={{ size: "1.6em", color: "#00C300" }}>
                    <FaLine />
                </IconContext.Provider>
            </a>
            <a
                href={`https://t.me/share/url?url=${encodedUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mr-2"
                aria-label="Share on Telegram"
            >
                <IconContext.Provider value={{ size: "1.6em", color: "#0088cc" }}>
                    <FaTelegram />
                </IconContext.Provider>
            </a>
            {/* WeChat deep links only open inside WeChat; keep for parity */}
            <a
                href={`weixin://dl/officialaccounts?scene=108&need_open_webview=0&url=${encodedUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mr-2"
                aria-label="Share on WeChat"
            >
                <IconContext.Provider value={{ size: "1.6em", color: "#1AAD19" }}>
                    <IoLogoWechat />
                </IconContext.Provider>
            </a>
            <a
                href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${shareText}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on Twitter/X"
                className="text-blue-400"
            >
                <FaTwitter size={28} />
            </a>
            <a
                href={`https://api.whatsapp.com/send?text=${shareText}%0A%0A${encodedUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on WhatsApp"
                className="text-green-500"
            >
                <FaWhatsapp size={28} />
            </a>
        </div>
    );
};

/* ------------------------------------------
   Main Page
-------------------------------------------*/

const ProductPage: React.FC = () => {
    const settings = JSON.parse(localStorage.getItem("settings") || "{}");
    const { t } = useTranslation(); // For translations

    const navigate = useNavigate();
    const { productId } = useParams<{ productId: string }>();
    const imageRef = useRef<HTMLDivElement>(null);
    const actionAddHandled = useRef(false);
    const [imgLoaded, setImgLoaded] = useState(false);
    const [contentMounted, setContentMounted] = useState(false);

    // Add-to-cart + modifiers hook (existing in your project)
    const {
        handleAddToCart,
        showModifierModal,
        handleCloseModifierModal,
        modifiers,
        selectedProduct,
        handleModifierSelection,
        handleConfirmModifiers,
        selectedModifiers,
    } = useHandleAddToCart();

    // Auth / customer session
    const { user: loggedInUser } = useAuth();
    const { selectedSessionCustomer } = useCustomerContext();

    const effectiveUser = useMemo(() => {
        const isAdminOrStaff = hasRole(["super_admin", "staff"]);
        const isAdminActingAs = isAdminOrStaff && selectedSessionCustomer;

        if (isAdminActingAs) {
            const customerRole = selectedSessionCustomer?.role || "dealer";
            return {
                id: selectedSessionCustomer.user_id,
                role: customerRole,
                isAuthenticated: true,
                customer_id: selectedSessionCustomer.id,
            };
        } else {
            let loggedInUserRole: string | undefined;
            let loggedInCustomerId: number | undefined;
            try {
                const rolesPermissionsItem = localStorage.getItem("rolesPermissions");
                if (rolesPermissionsItem) {
                    const rolesPermissions = JSON.parse(rolesPermissionsItem);
                    loggedInUserRole = rolesPermissions?.roles?.[0];
                }
                const storedCustomerId = localStorage.getItem("customer");
                if (storedCustomerId) {
                    const parsedId = JSON.parse(storedCustomerId);
                    if (typeof parsedId === "number") {
                        loggedInCustomerId = parsedId;
                    } else if (
                        typeof parsedId === "object" &&
                        parsedId !== null &&
                        typeof parsedId.id === "number"
                    ) {
                        loggedInCustomerId = parsedId.id;
                    }
                }
            } catch (e) {
                console.error("Failed to parse roles/customer ID in ProductPage:", e);
            }
            return {
                id: loggedInUser?.id,
                role: loggedInUserRole,
                isAuthenticated: !!loggedInUser,
                customer_id: loggedInCustomerId,
            };
        }
    }, [loggedInUser, selectedSessionCustomer]);

    /* ------------------------------------------
       React Query: product, customer, MT price
    -------------------------------------------*/
    const queryClient = useQueryClient();

    // Product query
    const {
        data: product,
        isLoading: loadingProduct,
        error: productError,
    } = useQuery<Product>({
        queryKey: ["product", productId],
        queryFn: () => fetchProductById(Number(productId), Number(effectiveUser.id)),
        enabled: !!productId,
        staleTime: 60_000, // cache freshness
    });

    // Prefetch: if URL slug implies a product path, prefetch by the given ID.
    useEffect(() => {
        if (!productId) return;
        // optimistic prefetch current product (no-op if already fetched)
        queryClient.prefetchQuery({
            queryKey: ["product", productId],
            queryFn: () => fetchProductById(Number(productId), Number(effectiveUser.id)),
            staleTime: 60_000,
        });
    }, [productId, queryClient]);

    // Customer query (effective customer)
    const {
        data: effectiveCustomerData,
        isLoading: loadingCustomerData,
        error: customerError,
    } = useQuery<Customer>({
        queryKey: ["customer", effectiveUser.customer_id],
        queryFn: () => fetchCustomer(effectiveUser.customer_id!),
        enabled: !!(effectiveUser.isAuthenticated && effectiveUser.customer_id),
        staleTime: 60_000,
    });

    // MT Price query
    const {
        data: mtPriceData,
        isLoading: loadingMtPrice,
        error: mtPriceError,
    } = useQuery<{ price: number; role: string } | null>({
        queryKey: ["mtPrice", product?.id, effectiveUser.id, effectiveUser.role],

        queryFn: async () => {
            const raw = await fetchMtPrice(
                product!.id,
                effectiveUser.id!,
                effectiveUser.role!
            );
            if (raw && typeof raw === "object" && product && product.id in raw) {
                return { price: raw[product.id].price, role: effectiveUser.role! };
            }
            return null;
        },
        enabled:
            !!product &&
            !!effectiveUser.isAuthenticated &&
            !!effectiveUser.id &&
            !!effectiveUser.role,
        staleTime: 30_000,
    });

    // Prefetch MT price as soon as the deps exist
    useEffect(() => {
        if (
            product &&
            effectiveUser.isAuthenticated &&
            effectiveUser.id &&
            effectiveUser.role
        ) {
            queryClient.prefetchQuery({
                queryKey: ["mtPrice", product.id, effectiveUser.id, effectiveUser.role],
                queryFn: async () => {
                    const raw = await fetchMtPrice(
                        product.id,
                        effectiveUser.id!,
                        effectiveUser.role!
                    );
                    if (raw && typeof raw === "object" && product.id in raw) {
                        return { price: raw[product.id].price, role: effectiveUser.role! };
                    }
                    return null;
                },
                staleTime: 30_000,
            });
        }
    }, [product, effectiveUser, queryClient]);

    /* ------------------------------------------
       Auto "action=add" once all data ready
    -------------------------------------------*/
    const handlePageAddToCartClick = useCallback(() => {
        if (!product) return;

        if (!effectiveUser.isAuthenticated) {
            toast.error("Please log in to add items to CART.");
            return;
        }

        if (loadingCustomerData) {
            toast.info("Checking customer details...");
            return;
        }

        if (!effectiveCustomerData) {
            toast.error(
                "Could not verify customer details. Please try again or contact support."
            );
            console.error(
                "Page Add to cart check failed: effectiveCustomerData is null"
            );
            return;
        }
        const hasPhone =
            effectiveCustomerData.phone &&
            String(effectiveCustomerData.phone).trim() !== "";
        const hasEmail =
            effectiveCustomerData.email &&
            String(effectiveCustomerData.email).trim() !== "";

        if (!hasPhone || !hasEmail) {
            toast.warn(
                "Please complete your profile (Phone and Email) before adding items."
            );
            setShowCustomerProfileModal(true);
        } else {
            const priceToAdd = mtPriceData?.price ?? product.product_value;

            handleAddToCart(
                product,
                imageRef as React.RefObject<HTMLImageElement>,
                priceToAdd,
                effectiveUser.id,
                effectiveUser.role
            );
        }
    }, [
        product,
        effectiveUser,
        loadingCustomerData,
        effectiveCustomerData,
        mtPriceData,
        handleAddToCart,
    ]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (
            params.get("action") === "add" &&
            !actionAddHandled.current &&
            product &&
            effectiveCustomerData
        ) {
            handlePageAddToCartClick();
            actionAddHandled.current = true;

            // remove 'action' from URL to avoid re-adding
            const newParams = new URLSearchParams(window.location.search);
            newParams.delete("action");
            const newSearch = newParams.toString();
            const newPath = `${window.location.pathname}${newSearch ? `?${newSearch}` : ""
                }`;
            window.history.replaceState({}, "", newPath);

            setTimeout(() => navigate("/cart"), 500);
        }
    }, [product, effectiveCustomerData, handlePageAddToCartClick, navigate]);

    // Basic mount fade-in
    useEffect(() => {
        const t = setTimeout(() => setContentMounted(true), 10);
        return () => clearTimeout(t);
    }, []);

    const [showCustomerProfileModal, setShowCustomerProfileModal] =
        useState(false);
    const handleCustomerProfileModalClose = () =>
        setShowCustomerProfileModal(false);

    /* ------------------------------------------
       Render
    -------------------------------------------*/
    if (loadingProduct) return <ProductSkeleton />;
    if (productError)
        return (
            <div className="container mx-auto p-4 text-center text-red-500">
                Error: {(productError as Error)?.message || "Failed to load product."}
            </div>
        );
    if (!product)
        return (
            <div className="container mx-auto p-4 text-center">
                Product not found.
            </div>
        );

    // Build share URL & SEO
    const shareableUrl =
        `${(window as any).APP_CONFIG?.ORIGIN_URL || ""}/product/${product.id}/${formatProductNameForUrl(
            product.product_name
        )}` || window.location.href;

    const productImage =
        product?.media && product.media.length > 0 && product.media[0].original_url
            ? product.media[0].original_url
            : `${window.location.origin}${(window as any).APP_CONFIG?.PUBLIC_URL || ""
            }/images/default_product_image.png`;

    const productNameNormal = product?.product_name || "Product";
    const productDescription = product?.product_desc || "No description available";

    // console.log(' effectiveUser.id, effectiveUser.role, effectiveUser.customer_id ', effectiveUser.id, effectiveUser.role, effectiveUser.customer_id);

    return (
        <>
            {/* SEO and Open Graph meta tags */}
            <Helmet>
                <title>{productNameNormal} | u8.my</title>
                <meta name="description" content={productDescription} />
                <meta property="og:title" content={productNameNormal} />
                <meta property="og:description" content={productDescription} />
                <meta property="og:image" content={productImage} />
                <meta property="og:url" content={shareableUrl} />
                <meta property="og:type" content="product" />
                <meta property="og:site_name" content="u8.my" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={productNameNormal} />
                <meta name="twitter:description" content={productDescription} />
                <meta name="twitter:image" content={productImage} />
            </Helmet>

            <div className="container mx-auto p-4">
                {/* Breadcrumbs / Back */}
                <div className="mb-4">
                    <Link to="/" className="text-blue-600 hover:underline">
                        &larr; Back to Products
                    </Link>
                </div>

                <FadeIn show={contentMounted}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Image Column */}
                        <div ref={imageRef}>
                            <FadeIn show={imgLoaded}>
                                <img
                                    src={
                                        product.media.length > 0
                                            ? product.media[0].original_url
                                            : "placeholder.jpg"
                                    }
                                    alt={product.product_name}
                                    className="w-full h-auto object-contain rounded shadow-md"
                                    onLoad={() => setImgLoaded(true)}
                                />
                            </FadeIn>
                            {!imgLoaded && (
                                <div className="w-full h-96 bg-gray-200 rounded shadow-md animate-pulse" />
                            )}
                            {/* TODO: add thumbnails if multiple images */}
                        </div>

                        {/* Details Column */}
                        <div>
                            <h1 className="text-3xl font-bold mb-3">
                                {product.product_name}
                            </h1>
                            <p className="text-md text-gray-500 mb-4">
                                SKU: {product.product_code}
                            </p>
                            {product.cat_name && (
                                <p className="text-sm text-gray-600 mb-1">
                                    Category: {product.cat_name}
                                </p>
                            )}
                            {product.brand_name && (
                                <p className="text-sm text-gray-600 mb-4">
                                    Brand: {product.brand_name}
                                </p>
                            )}

                            {/* Price */}
                            {settings?.themes?.displayPrice === true ? (
                                <div className="price-div mb-5 p-3 bg-gray-100 rounded min-h-[6rem]">
                                    {loadingMtPrice && effectiveUser.isAuthenticated ? (
                                        <div className="animate-pulse space-y-2">
                                            <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                                            <div className="h-5 bg-gray-200 rounded w-1/3"></div>
                                        </div>
                                    ) : (
                                        <Fragment>
                                            {mtPriceError && (
                                                <p className="text-sm text-red-500">
                                                    {(mtPriceError as Error)?.message ||
                                                        "Failed to load specific price."}
                                                </p>
                                            )}

                                            {effectiveUser.isAuthenticated && mtPriceData && (
                                                <p className="text-xl text-green-700 font-semibold mb-1">
                                                    Your VIP Price:{" "}
                                                    <span className="font-bold">
                                                        {displayPrice(mtPriceData.price)}
                                                    </span>
                                                </p>
                                            )}
                                            <p
                                                className={`text-lg text-gray-800 ${mtPriceData ? " text-sm" : ""
                                                    }`}
                                            >
                                                <span>{t('productCard.srp')}:&nbsp;</span>

                                                {product.SRP > 0 && product.SRP !== product.product_value ? (
                                                    <span className='text-gray-400 font-thin text-lg line-through'>{displayPrice(product.SRP)}</span>
                                                ) : null}

                                                <span className='text-gray-600 font-semibold text-lg  px-2'>{displayPrice(product.product_value)}</span>
                                            </p>
                                        </Fragment>
                                    )}
                                </div>
                            ) : null}

                            {/* Description */}
                            <div className="prose max-w-none mb-6">
                                <h3 className="text-lg font-semibold mb-2">Description</h3>
                                <p>{product.product_desc || "No description available."}</p>
                            </div>

                            {/* Stock/Warranty/Link Info */}
                            <div className="flex flex-col mb-4">
                                <div className="flex flex-row justify-between mt-2">
                                    {product?.stock !== undefined && product?.stock > 0 ? (
                                        <span className="productCardStock px-2 py-1 my-1 text-white bg-green-600 border rounded-lg shadow">
                                            In Stock
                                            {settings?.themes?.displayStockQty === true ? (
                                                <span className="stock-qty">&nbsp;{product?.stock}</span>
                                            ) : null}
                                        </span>
                                    ) : (
                                        <span className="productCardStock px-2 py-1 my-1 text-white bg-orange-600 border rounded-lg shadow">
                                            Back Order
                                            {settings?.themes?.displayStockQty === true ? (
                                                <span className="backorder-qty">
                                                    &nbsp;{product?.stock}
                                                </span>
                                            ) : null}
                                        </span>
                                    )}

                                    {product?.restock !== undefined && product?.restock > 0 && (
                                        <span className="productCardStock px-2 py-1 my-1 text-white bg-purple-600 border rounded-lg shadow flex flex-row">
                                            Re-Stock
                                            {settings?.themes?.displayStockQty === true ? (
                                                <span className="restock-qty">
                                                    &nbsp;{product?.restock}
                                                </span>
                                            ) : null}
                                            {settings?.themes?.displayRestockAt === true &&
                                                product.restock_at ? (
                                                <span className="restock-dt">
                                                    &nbsp;on{" "}
                                                    {new Date(product.restock_at).toLocaleDateString(
                                                        "en-GB",
                                                        { day: "2-digit", month: "2-digit", year: "2-digit" }
                                                    )}
                                                </span>
                                            ) : null}
                                        </span>
                                    )}

                                    <div className="flex flex-row justify-between items-center mt-2">
                                        {product.product_link && (
                                            <>
                                                <a
                                                    href={product.product_link}
                                                    className="mr-2"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    data-tooltip-id={`link-${product.id}`}
                                                    data-tooltip-content={`External ${product.product_name} Product Details Link`}
                                                >
                                                    <IconContext.Provider
                                                        value={{ size: "1em", color: "purple" }}
                                                    >
                                                        <FaLink />
                                                    </IconContext.Provider>
                                                </a>
                                                <ReactTooltip
                                                    id={`link-${product.id}`}
                                                    place="top"
                                                    style={{
                                                        backgroundColor: "yellow",
                                                        color: "#000000",
                                                        zIndex: 50,
                                                    }}
                                                />
                                            </>
                                        )}
                                        {product.warranty && (
                                            <>
                                                <div
                                                    data-tooltip-id={`warranty-${product.id}`}
                                                    data-tooltip-content={`${product.warranty} warranty`}
                                                >
                                                    <IconContext.Provider
                                                        value={{ size: "1em", color: "brown" }}
                                                    >
                                                        <FaShieldHeart />
                                                    </IconContext.Provider>
                                                </div>
                                                <ReactTooltip
                                                    id={`warranty-${product.id}`}
                                                    place="top"
                                                    style={{
                                                        backgroundColor: "yellow",
                                                        color: "#000000",
                                                        zIndex: 50,
                                                    }}
                                                />
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Add to Cart */}
                            {effectiveUser.isAuthenticated ? (
                                <button
                                    onClick={handlePageAddToCartClick}
                                    disabled={loadingCustomerData || loadingMtPrice}
                                    className={`w-full px-6 py-3 bg-blue-600 text-white rounded hover:bg-blue-700 transition duration-150 text-lg font-semibold ${loadingCustomerData || loadingMtPrice
                                        ? "opacity-50 cursor-wait"
                                        : ""
                                        }`}
                                >
                                    {loadingCustomerData
                                        ? "Checking Details..."
                                        : loadingMtPrice
                                            ? "Getting Price..."
                                            : "Add to Cart"}
                                </button>
                            ) : (
                                <p className="text-center mt-4">
                                    <Link
                                        to="/login"
                                        className="text-blue-600 hover:underline font-semibold"
                                    >
                                        Log in
                                    </Link>{" "}
                                    to purchase.
                                </p>
                            )}

                            {customerError && (
                                <p className="text-sm text-red-500 mt-2">
                                    {(customerError as Error)?.message ||
                                        "Failed to load customer details."}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Social Share */}
                    <div className="mt-6">
                        <SocialShareButtons
                            url={shareableUrl}
                            text={`Check out this product: ${product?.product_name}`}
                        />
                    </div>

                    {/* Details Section */}
                    {product.details && <ProductDetails product={product} />}

                    {/* Customer Profile Modal */}
                    {showCustomerProfileModal && effectiveCustomerData && (
                        <CustomerProfileModal
                            initialCustomerData={effectiveCustomerData}
                            onClose={handleCustomerProfileModalClose}
                        />
                    )}

                    {/* Modifier Modal */}
                    {showModifierModal && selectedProduct && (
                        <ModifierModal
                            product={selectedProduct}
                            modifiers={modifiers}
                            onClose={handleCloseModifierModal}
                            onModifierSelection={handleModifierSelection}
                            onConfirm={handleConfirmModifiers}
                            selectedModifiers={selectedModifiers}
                        />
                    )}
                </FadeIn>
            </div>
        </>
    );
};

export default ProductPage;
